class HeroSlideshow extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5000;
    this.transitionDuration = parseFloat(this.dataset.transitionDuration || '1') * 1000;
    this.containers = {
      desktop: this.querySelector('.hero-slideshow__slides--desktop'),
      mobile: this.querySelector('.hero-slideshow__slides--mobile'),
    };
    this.slides = {
      desktop: this.containers.desktop
        ? Array.from(this.containers.desktop.querySelectorAll('.hero-slideshow__slide'))
        : [],
      mobile: this.containers.mobile
        ? Array.from(this.containers.mobile.querySelectorAll('.hero-slideshow__slide'))
        : [],
    };
    this.orders = null;
    this.activeGroup = null;
    this.activeSlideEl = null;
    this.nextTimeout = null;
    this.resizeHandler = null;
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.mediaQuery = window.matchMedia ? window.matchMedia('(orientation: landscape)') : null;
    this.touchStartX = 0;
    this.handleTouchStart = null;
    this.handleTouchEnd = null;
  }

  get visibleSlides() {
    if (!this.activeGroup) {
      this.activeGroup = this.determineActiveGroup();
    }
    return this.getSlidesForGroup(this.activeGroup);
  }

  connectedCallback() {
    if (this.dataset.randomize === 'true') {
      this.shuffleSlides();
    }

    const slides = this.visibleSlides;

    if (!slides.length) return;

    this.setActiveSlide(slides[this.currentSlide] || slides[0]);

    requestAnimationFrame(() => {
      this.classList.add('hero-slideshow--ready');
    });

    this.bindOrientationListener();

    if (slides.length > 1) {
      this.queueNextSlide(true);
    }
    this.querySelector('.hero-slideshow__nav--prev')?.addEventListener('click', () => this.prevSlide());
    this.querySelector('.hero-slideshow__nav--next')?.addEventListener('click', () => this.nextSlide());
    this.bindTouchListeners();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.unbindOrientationListener();
    this.unbindTouchListeners();
  }

  shuffleSlides() {
    this.orders = {};

    ['desktop', 'mobile'].forEach((group) => {
      const slides = this.slides[group];
      if (!slides || slides.length <= 1) return;

      const order = slides.map((_, index) => index);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      this.orders[group] = order;
    });

    this.currentSlide = 0;
  }

  goToSlide(index) {
    const slides = this.visibleSlides;
    if (!slides.length) return;

    const safeIndex = ((index % slides.length) + slides.length) % slides.length;
    this.currentSlide = safeIndex;

    this.setActiveSlide(slides[safeIndex]);
    this.queueNextSlide();
  }

  nextSlide() {
    const slides = this.visibleSlides;
    if (!slides.length) return;

    const nextIndex = (this.currentSlide + 1) % slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const slides = this.visibleSlides;
    if (!slides.length) return;

    const prevIndex = (this.currentSlide - 1 + slides.length) % slides.length;
    this.goToSlide(prevIndex);
  }

  queueNextSlide(skipTransitionDelay = false) {
    this.stopAutoplay();

    const delay = (skipTransitionDelay ? 0 : this.transitionDuration) + this.autoplaySpeed;
    this.nextTimeout = window.setTimeout(() => {
      this.nextSlide();
    }, delay);
  }

  stopAutoplay() {
    if (this.nextTimeout) {
      clearTimeout(this.nextTimeout);
      this.nextTimeout = null;
    }
  }

  getSlidesForGroup(group) {
    if (!group) return [];
    const slides = this.slides[group] || [];
    if (!slides.length) return [];

    if (!this.orders) return slides;

    const order = this.orders[group];
    if (!order || order.length !== slides.length) {
      return slides;
    }

    return order.map((index) => slides[index]).filter(Boolean);
  }

  setActiveSlide(slide) {
    if (this.activeSlideEl === slide) return;

    this.querySelector('.hero-slideshow__slide--previous')?.classList.remove('hero-slideshow__slide--previous');

    if (this.activeSlideEl) {
      this.activeSlideEl.classList.replace('hero-slideshow__slide--active', 'hero-slideshow__slide--previous');
    }

    if (slide) {
      slide.classList.add('hero-slideshow__slide--active');
      this.activeSlideEl = slide;
    } else {
      this.activeSlideEl = null;
    }
  }

  determineActiveGroup() {
    const hasDesktop = this.slides.desktop.length > 0;
    const hasMobile = this.slides.mobile.length > 0;

    if (!hasDesktop && !hasMobile) {
      return null;
    }

    if (hasDesktop && hasMobile) {
      const prefersLandscape = this.mediaQuery ? this.mediaQuery.matches : window.innerWidth >= window.innerHeight;
      return prefersLandscape ? 'desktop' : 'mobile';
    }

    return hasDesktop ? 'desktop' : 'mobile';
  }

  handleMediaChange() {
    const nextGroup = this.determineActiveGroup();
    if (nextGroup === this.activeGroup) return;

    this.activeGroup = nextGroup;
    this.currentSlide = 0;

    const slides = this.visibleSlides;
    this.setActiveSlide(slides[0] || null);

    if (slides.length <= 1) {
      this.stopAutoplay();
      return;
    }

    this.queueNextSlide(true);
  }

  bindOrientationListener() {
    if (this.mediaQuery && this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleMediaChange);
    }
  }

  unbindOrientationListener() {
    if (this.mediaQuery && this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }

  bindTouchListeners() {
    const onTouchStart = (/** @type {TouchEvent} */ e) => {
      const touch = e.touches[0];
      if (!touch) return;
      this.touchStartX = touch.clientX;
    };
    const onTouchEnd = (/** @type {TouchEvent} */ e) => {
      const touch = e.changedTouches[0];
      if (!touch) return;
      const delta = touch.clientX - this.touchStartX;
      if (Math.abs(delta) > 50) {
        if (delta < 0) this.nextSlide();
        else this.prevSlide();
      }
    };
    this.handleTouchStart = onTouchStart;
    this.handleTouchEnd = onTouchEnd;
    this.addEventListener('touchstart', onTouchStart, { passive: true });
    this.addEventListener('touchend', onTouchEnd, { passive: true });
  }

  unbindTouchListeners() {
    const start = this.handleTouchStart;
    const end = this.handleTouchEnd;
    if (!start || !end) return;
    this.removeEventListener('touchstart', start);
    this.removeEventListener('touchend', end);
  }
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
